/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useEffect, useState } from 'react';
import { Modal, Radio, Spin, Button } from 'antd';
import { useIntl } from 'react-intl';
import { useThemeSwitcher } from 'react-css-theme-switcher';
import doReturnTheme from '~src/styles/themeEngine';
import icon from '~components/_common/icon/icon';

const COOKIE_NAME = 'lang';
const LEGACY_COOKIE_NAME = 'language';
const DEFAULT_LANG = 'da';
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

const LanguageSettingModal = ({ state, onCancel }) => {
  const { currentTheme } = useThemeSwitcher();
  const theme = doReturnTheme(currentTheme);
  const intl = useIntl();

  const [selectedLang, setSelectedLang] = useState(DEFAULT_LANG);
  const [isLoading, setIsLoading] = useState(false);

  const getRootCookieDomain = (): string | null => {
    // We only want to force the root domain cookie when we are on *.c-web.dk
    // Otherwise (localhost, IPs, etc.) setting "domain=.c-web.dk" would fail.
    const host = window.location.hostname || '';
    if (host === 'c-web.dk' || host.endsWith('.c-web.dk')) return '.c-web.dk';
    return null;
  };

  const getCookieValue = (name: string): string | null => {
    const match = document.cookie.match(new RegExp(`(^|;\\s*)${name}=([^;]*)`));
    return match ? decodeURIComponent(match[2]) : null;
  };

  const setLangCookie = (lang: string | null) => {
    const domain = getRootCookieDomain();

    if (lang) {
      const parts = [
        `${COOKIE_NAME}=${encodeURIComponent(lang)}`,
        `max-age=${ONE_YEAR_SECONDS}`,
        'path=/',
        'SameSite=Lax',
      ];

      if (domain) parts.push(`domain=${domain}`);

      document.cookie = parts.join('; ');
      return;
    }

    // delete (try with domain root + without, to be safe)
    const deletePartsBase = [
      `${COOKIE_NAME}=`,
      'expires=Thu, 01 Jan 1970 00:00:00 GMT',
      'path=/',
      'SameSite=Lax',
    ];

    document.cookie = deletePartsBase.join('; ');
    if (domain)
      document.cookie = [...deletePartsBase, `domain=${domain}`].join('; ');
  };

  const deleteLegacyCookie = () => {
    const domain = getRootCookieDomain();
    const partsBase = [
      `${LEGACY_COOKIE_NAME}=`,
      'expires=Thu, 01 Jan 1970 00:00:00 GMT',
      'path=/',
      'SameSite=Lax',
    ];

    document.cookie = partsBase.join('; ');
    if (domain) document.cookie = [...partsBase, `domain=${domain}`].join('; ');
  };

  useEffect(() => {
    // 1) Prefer new cookie
    const cookieLang = getCookieValue(COOKIE_NAME);

    if (cookieLang) {
      setSelectedLang(cookieLang);
      return;
    }

    // 2) Migrate legacy cookie if exists
    const legacyLang = getCookieValue(LEGACY_COOKIE_NAME);
    if (legacyLang) {
      setSelectedLang(legacyLang);
      setLangCookie(legacyLang);
      deleteLegacyCookie();
      return;
    }

    // 3) Default fallback per ticket: Danish
    setSelectedLang(DEFAULT_LANG);
  }, [state]);

  const onSaveLanguage = () => {
    setIsLoading(true);
    setLangCookie(selectedLang);
    setTimeout(() => {
      setIsLoading(false);
      window.location.reload();
    }, 500);
  };

  const onResetLanguage = () => {
    setIsLoading(true);
    // Reset = go to default (da) and persist it, not delete cookie
    setSelectedLang(DEFAULT_LANG);
    setLangCookie(DEFAULT_LANG);

    // optional cleanup
    deleteLegacyCookie();
    setTimeout(() => {
      setIsLoading(false);
      window.location.reload();
    }, 500);
  };

  return (
    <Modal
      title={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            fontSize: '18px',
            fontWeight: 'bold',
          }}>
          {icon('fas', 'earth-europe', {
            color: theme.colors.secondary,
            fontSize: '20px',
          })}
          {intl.formatMessage({ id: 'change-language' })}
        </div>
      }
      centered
      visible={state}
      onOk={onSaveLanguage}
      onCancel={onCancel}
      okText={intl.formatMessage({ id: 'save' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      closeIcon={icon(
        'far',
        'times',
        { color: theme.colors.darkGray, marginTop: '18px', fontSize: '20px' },
        'close'
      )}
      width={420}
      footer={[
        // @ts-ignore
        <Button key="reset" type="danger" onClick={onResetLanguage}>
          {intl.formatMessage({ id: 'reset' })}
        </Button>,
        <Button key="cancel" onClick={onCancel}>
          {intl.formatMessage({ id: 'cancel' })}
        </Button>,
        <Button key="save" type="primary" onClick={onSaveLanguage}>
          {intl.formatMessage({ id: 'save' })}
        </Button>,
      ]}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <b>{intl.formatMessage({ id: 'select-language' })}</b>
        <Radio.Group
          onChange={(e) => setSelectedLang(e.target.value)}
          value={selectedLang}
          style={{ marginTop: '10px' }}>
          <Radio value="da">Danish</Radio>
          <Radio value="en">English</Radio>
          {/*<Radio value="sv">Swedish</Radio>*/}
          {/*<Radio value="de">German</Radio>*/}
        </Radio.Group>
      </div>
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.25)',
            top: 0,
            left: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <Spin size="large" />
        </div>
      )}
    </Modal>
  );
};

export default LanguageSettingModal;
